import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Bell, Loader2, CheckCheck, Inbox as InboxIcon, Wallet, CheckCircle2, XCircle,
  Clock, Sparkles, Megaphone, PartyPopper, ChevronDown, ExternalLink, Lock,
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// BOÎTE DE RÉCEPTION TeckiA (lecture seule) — messages de l'application vers l'utilisateur :
// paiement déclaré / validé / rejeté, crédits ajoutés, bienvenue, annonces de l'équipe.
// L'utilisateur ne peut pas répondre.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const TYPE_STYLE = {
  payment_validated: { icon: CheckCircle2, color: 'text-emerald-300', bg: 'bg-emerald-500/15', ring: 'border-emerald-500/30', label: 'Paiement' },
  payment_declared:  { icon: Clock,        color: 'text-amber-300',   bg: 'bg-amber-500/15',   ring: 'border-amber-500/30',   label: 'Paiement' },
  payment_rejected:  { icon: XCircle,      color: 'text-rose-300',    bg: 'bg-rose-500/15',    ring: 'border-rose-500/30',    label: 'Paiement' },
  credits_added:     { icon: Wallet,       color: 'text-emerald-300', bg: 'bg-emerald-500/15', ring: 'border-emerald-500/30', label: 'Crédits' },
  welcome:           { icon: PartyPopper,  color: 'text-fuchsia-300', bg: 'bg-fuchsia-500/15', ring: 'border-fuchsia-500/30', label: 'Bienvenue' },
  promo:             { icon: Sparkles,     color: 'text-yellow-300',  bg: 'bg-yellow-500/15',  ring: 'border-yellow-500/30',  label: 'Offre' },
  info:              { icon: Megaphone,    color: 'text-sky-300',     bg: 'bg-sky-500/15',     ring: 'border-sky-500/30',     label: 'Annonce' },
};
const styleOf = (t) => TYPE_STYLE[t] || TYPE_STYLE.info;

// module du message -> vue de l'accueil à ouvrir
const MODULE_VIEW = {
  ia_studio_video: { view: 'ia_studio_video', label: 'Ouvrir IA Studio Vidéo' },
  ia_studio_image: { view: 'ia_studio', label: 'Ouvrir IA Studio Image' },
  voice_split: { view: 'voice_split', label: 'Ouvrir Voice Kar' },
  utile: { view: 'utile', label: 'Ouvrir TeckiA Utile' },
  facebook_page: { view: 'fb_connect', label: 'Ouvrir ma Page Facebook' },
  whatsapp: { view: 'whatsapp_connect', label: 'Ouvrir WhatsApp' },
  cm_studio: { view: 'fb_connect', label: 'Ouvrir ma Page Facebook' },
  support: { view: 'support', label: 'Ouvrir le support' },
};

const FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'unread', label: 'Non lus' },
  { key: 'payments', label: 'Paiements' },
  { key: 'news', label: 'Annonces' },
];

export function timeAgo(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  if (s < 7 * 86400) return `il y a ${Math.floor(s / 86400)} j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Compteur de notifications non lues (interrogé toutes les 5 s, en pause si l'onglet est caché)
function useUnread() {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const res = await fetch(`${API_URL}/api/notifications/unread-count`, { headers: authHeaders() });
        const d = await res.json();
        if (alive && d.success) setUnread(d.unread);
      } catch { /* silencieux */ }
    };
    tick();
    const t = setInterval(() => { if (!document.hidden) tick(); }, 5000);
    const vis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', vis);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', vis); };
  }, []);
  return unread;
}

// ── Cloche (en-tête de l'accueil) : devient ROUGE avec texte blanc dès qu'il y a des messages non lus ──
export function NotificationBell({ onClick }) {
  const unread = useUnread();
  const hasUnread = unread > 0;
  return (
    <button
      onClick={onClick}
      title="Notifications"
      className={`relative flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl transition-all hover:scale-105 text-sm md:text-base ${
        hasUnread
          ? 'bg-red-600 hover:bg-red-500 text-white font-semibold shadow-lg shadow-red-600/40'
          : 'bg-sky-500/25 hover:bg-sky-500/40 border border-sky-400/40 text-sky-100'}`}
    >
      <Bell className={`w-4 h-4 ${hasUnread ? 'animate-pulse' : ''}`} />
      <span className="hidden sm:inline">Notification</span>
      {hasUnread && (
        <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 rounded-full bg-white text-red-600 border-2 border-red-600 text-[11px] font-extrabold flex items-center justify-center shadow-lg">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}

// ── Bannière rouge affichée en haut de TOUTES les pages de modules quand il y a des messages non lus ──
export function UnreadBanner({ onOpen }) {
  const unread = useUnread();
  if (unread <= 0) return null;
  return (
    <button
      onClick={onOpen}
      className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-gradient-to-r from-red-700 via-red-600 to-rose-600 hover:from-red-600 hover:to-rose-500 text-white text-sm font-semibold shadow-lg shadow-red-900/40 transition"
    >
      <Bell className="w-4 h-4 animate-pulse" />
      <span>
        {unread === 1 ? 'Vous avez 1 nouvelle notification' : `Vous avez ${unread} nouvelles notifications`} — cliquez pour lire
      </span>
    </button>
  );
}

// ── Page boîte de réception ─────────────────────────────────────────────────
const PAGE = 20;

export default function Inbox({ onBack, onOpenModule }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const offsetRef = useRef(0);

  const fetchPage = useCallback(async (reset) => {
    if (reset) { setLoading(true); offsetRef.current = 0; } else setLoadingMore(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/notifications?limit=${PAGE}&offset=${offsetRef.current}`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) {
        setItems(prev => reset ? d.notifications : [...prev, ...d.notifications]);
        setTotal(d.total); setUnread(d.unread);
        offsetRef.current += d.notifications.length;
      } else setError(d.message || 'Chargement impossible.');
    } catch { setError('Erreur réseau.'); }
    setLoading(false); setLoadingMore(false);
  }, []);

  useEffect(() => { fetchPage(true); }, [fetchPage]);

  const markRead = async (ids) => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/read`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(ids === 'all' ? { all: true } : { ids }),
      });
      const d = await res.json();
      if (d.success) setUnread(d.unread);
    } catch { /* silencieux */ }
  };

  const toggle = (n) => {
    const opening = openId !== n.id;
    setOpenId(opening ? n.id : null);
    if (opening && !n.is_read) {
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      markRead([n.id]);
    }
  };

  const markAll = async () => {
    setItems(prev => prev.map(x => ({ ...x, is_read: true })));
    await markRead('all');
  };

  const shown = items.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'payments') return n.type.startsWith('payment') || n.type === 'credits_added';
    if (filter === 'news') return ['info', 'promo', 'welcome'].includes(n.type);
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 text-white">
      <div className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        {/* en-tête */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/20">
                  <InboxIcon className="w-5 h-5" />
                </span>
                Boîte de réception
              </h1>
              <p className="text-sm text-blue-200 mt-0.5">
                {unread > 0 ? `${unread} message${unread > 1 ? 's' : ''} non lu${unread > 1 ? 's' : ''}` : 'Vous êtes à jour'}
              </p>
            </div>
          </div>
          <button onClick={markAll} disabled={unread === 0}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-30 text-sm transition">
            <CheckCheck className="w-4 h-4" /> <span className="hidden sm:inline">Tout marquer comme lu</span>
          </button>
        </div>

        {/* filtres */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap border transition ${
                filter === f.key ? 'bg-blue-500/30 border-blue-400/60 text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>
              {f.label}
            </button>
          ))}
        </div>

        {error && <p className="text-rose-300 text-sm mb-3">{error}</p>}

        {loading ? (
          <div className="py-20 text-center"><Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto" /></div>
        ) : shown.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-20 h-20 mx-auto rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
              <Bell className="w-9 h-9 text-gray-500" />
            </div>
            <p className="text-gray-300 font-medium">Aucun message</p>
            <p className="text-gray-500 text-sm mt-1">
              {filter === 'all' ? "Vous serez averti ici dès qu'il y a du nouveau sur votre compte." : 'Rien dans ce filtre pour le moment.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {shown.map(n => {
              const st = styleOf(n.type);
              const Icon = st.icon;
              const open = openId === n.id;
              const mod = MODULE_VIEW[n.module];
              const d = n.data || {};
              return (
                <div key={n.id}
                  className={`rounded-2xl border transition-all overflow-hidden ${
                    n.is_read ? 'bg-white/[0.03] border-white/10' : `bg-white/[0.07] ${st.ring}`}`}>
                  <button onClick={() => toggle(n)} className="w-full text-left flex items-start gap-3.5 p-4">
                    <span className={`w-11 h-11 rounded-2xl ${st.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${st.color}`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className={`text-[15px] leading-snug ${n.is_read ? 'text-gray-200 font-medium' : 'text-white font-bold'}`}>{n.title}</h3>
                        <span className="text-[11px] text-gray-500 whitespace-nowrap mt-0.5">{timeAgo(n.created_at)}</span>
                      </div>
                      {!open && <p className="text-sm text-gray-400 mt-1 line-clamp-2">{n.body}</p>}
                    </div>
                    <div className="flex flex-col items-center gap-2 pt-1">
                      {!n.is_read && <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-rose-400 to-pink-400" />}
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {open && (
                    <div className="px-4 pb-4 pl-[4.6rem]">
                      <p className="text-sm text-gray-200 whitespace-pre-line leading-relaxed">{n.body}</p>
                      {(d.amount_ar || d.method || d.ref) && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {d.amount_ar ? <span className="px-2.5 py-1 rounded-lg bg-white/10 text-xs text-gray-200">💰 {Number(d.amount_ar).toLocaleString('fr-FR')} Ar</span> : null}
                          {d.method ? <span className="px-2.5 py-1 rounded-lg bg-white/10 text-xs text-gray-200">📲 {d.method}</span> : null}
                          {d.ref ? <span className="px-2.5 py-1 rounded-lg bg-white/10 text-xs text-gray-200">🔖 Réf. {d.ref}</span> : null}
                          {d.balance != null ? <span className="px-2.5 py-1 rounded-lg bg-emerald-500/15 text-xs text-emerald-200">Solde : {Number(d.balance).toLocaleString('fr-FR')} Ar</span> : null}
                        </div>
                      )}
                      {mod && onOpenModule && (
                        <button onClick={() => onOpenModule(mod.view)}
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-fuchsia-600 hover:opacity-90 text-sm font-medium transition">
                          {mod.label} <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {items.length < total && (
              <button onClick={() => fetchPage(false)} disabled={loadingMore}
                className="w-full py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-gray-300 transition">
                {loadingMore ? 'Chargement…' : `Charger plus (${total - items.length} restants)`}
              </button>
            )}
          </div>
        )}

        <p className="mt-8 flex items-center justify-center gap-1.5 text-xs text-gray-500">
          <Lock className="w-3 h-3" /> Messages automatiques de TeckiA — cette boîte est en lecture seule.
        </p>
      </div>
    </div>
  );
}
